import { Box, Modal } from "@mui/material";
import { FileDownload, Link, Close, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CopyToClipboard from "react-copy-to-clipboard";
import { ButtonV1 } from "../../components/atoms/button";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../components/organisms/ImageGallery.css";
import { downloadImagesAsZip } from "../../utils/helper";
import { useGetCreativeByIdQuery } from "../../api/creative";

export default function ImageGallery() {
    const [copy, setCopy] = useState(false);
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [URLSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const sliderRef = useRef<Slider>(null);
    const creativeId = URLSearchParams.get("creativeId") || "";

    // Fetch creative data
    const { data: creativeData, isLoading, isError } = useGetCreativeByIdQuery(creativeId);

    // Get images from API response
    const images: string[] = creativeData?.data?.imageGallery || [];

    // Get title from API response or URL params as fallback
    const title = creativeData?.data?.title ||
        creativeData?.data?.newTitle ||
        decodeURIComponent(URLSearchParams.get("title") || "Image Gallery");

    // Get newTitle for downloads - prioritize newTitle over title
    const downloadTitle = creativeData?.data?.newTitle || creativeData?.data?.title || "Image Gallery";

    const handleClose = () => {
        navigate(-1); // Go back to previous page
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowLeft':
                    sliderRef.current?.slickPrev();
                    break;
                case 'ArrowRight':
                    sliderRef.current?.slickNext();
                    break;
                case 'Escape':
                    handleClose();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const style = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "100vw",
        bgcolor: "#000000",
        border: "none",
        boxShadow: 4,
        p: 4,
    };

    /*
    <button><ChevronRight className="h-10 w-10 text-white" /></button>
     */

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        beforeChange: (oldIndex: number, newIndex: number) => setCurrentSlide(newIndex),
        customPaging: (i: number) => (
            <div
                className={`w-3 h-3 mx-1 rounded-sm transition-all duration-200 ${i === currentSlide ? "bg-black" : "bg-black/30 border border-black/50"
                    }`}
            />
        ),
        prevArrow: <button className="absolute left-5 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
            <ChevronLeft className="!h-10 !w-10 text-black" />
        </button>,
        nextArrow: <button className="absolute right-5 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
            <ChevronRight className="!h-10 !w-10 text-black" />
        </button>,
        // Keep dots constrained to the bottom-right inside the gallery
        dotsClass: "slick-dots custom-dots",
        autoplay: false,
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    arrows: true,
                    dots: true
                }
            }
        ]
    };

    // Create shareable URL
    const shareableUrl = `${window.location.origin}/imageGallery?creativeId=${creativeId}`;

    const handleDownloadAll = async () => {
        setIsDownloading(true);
        try {
            await downloadImagesAsZip(images, downloadTitle);
        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="bg-black h-[100vh] flex justify-center items-center">
                <div className="text-white text-xl">Loading...</div>
            </div>
        );
    }

    // Error state
    if (isError || !creativeData) {
        return (
            <Modal open={true} onClose={handleClose}>
                <Box sx={style}>
                    <div className="text-white text-center">
                        <h2 className="text-2xl mb-4">Error loading creative</h2>
                        <ButtonV1 onClick={handleClose}>Close</ButtonV1>
                    </div>
                </Box>
            </Modal>
        );
    }

    // No images state
    if (!images || images.length === 0) {
        return (
            <Modal open={true} onClose={handleClose}>
                <Box sx={style}>
                    <div className="text-white text-center">
                        <h2 className="text-2xl mb-4">No images found</h2>
                        <ButtonV1 onClick={handleClose}>Close</ButtonV1>
                    </div>
                </Box>
            </Modal>
        );
    }

    return (
        <div className="bg-black h-[100vh] flex justify-between flex-col p-4 sm:p-8 gap-4">
            {/* Image Carousel */}
            <div className="h-[80%] sm:h-[90%] w-full mx-auto flex items-center rounded-2xl justify-center bg-white relative">
                {images.length === 1 ? (
                    // Single image - no carousel needed
                    <img
                        src={images[0]}
                        alt={title}
                        className="max-h-[80%] max-w-full object-contain"
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "https://via.placeholder.com/800x600/8B5CF6/FFFFFF?text=Image+Error";
                        }}
                    />
                ) : (
                    // Multiple images - show carousel
                    <div className="relative image-gallery-modal w-full h-full">
                        <Slider ref={sliderRef} {...sliderSettings} className="w-full h-full">
                            {images.map((image, index) => (
                                <div key={index} className="h-[100%] flex items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center">
                                        <img
                                            src={image}
                                            alt={`${title} - Image ${index + 1}`}
                                            className="h-[100%] max-w-full object-contain mx-auto"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = "https://via.placeholder.com/800x600/8B5CF6/FFFFFF?text=Image+Error";
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex w-full mx-auto justify-between items-center h-[15%] sm:h-[10%] py-4">
                <div className="flex-1">
                    <h1 className="text-wrap whitespace-normal text-xl text-white font-medium">
                        {title}
                    </h1>
                    {images.length > 1 && (
                        <p className="text-white/70 text-sm mt-1">
                            {currentSlide + 1} / {images.length} Frames
                        </p>
                    )}
                </div>
                <div className="flex gap-4 relative">
                    <ButtonV1
                        type="button"
                    >
                        <CopyToClipboard
                            text={shareableUrl}
                            onCopy={() => setCopy(true)}
                        >
                            {copy ? (
                                <p className="capitalize">copied</p>
                            ) : (
                                <Link className="text-white" />
                            )}
                        </CopyToClipboard>
                    </ButtonV1>
                    <ButtonV1
                        type="button"
                        onClick={isDownloading ? undefined : handleDownloadAll}
                        className={isDownloading ? "opacity-50 cursor-not-allowed" : ""}
                    >
                        {isDownloading ? (
                            <span className="text-white text-xs">Downloading...</span>
                        ) : (
                            <FileDownload className="text-white" />
                        )}
                    </ButtonV1>
                </div>
            </div>
        </div>
    );
}